import Image from 'next/image';
import type { IFeatureQuote } from '@/interfaces/feature';
import type { Language } from '@/lib/translations';
import { getThemeBackgroundClass, getThemeTextClass } from '@/lib/theme';
import type { ITheme } from '@/types/theme';

export default function FeatureStudioQuote({
  quote,
  language,
  theme,
}: {
  quote: IFeatureQuote;
  language: Language;
  theme: ITheme;
}) {
  return (
    <section
      className="bg-[var(--color-background)] py-16 sm:py-20"
      aria-label={language === 'en' ? quote.label_en : quote.label_cn}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <figure
          className={`grid overflow-hidden rounded-[2rem] border border-[var(--color-card-border)] md:grid-cols-[0.9fr_1.1fr] ${getThemeBackgroundClass(theme, 'secondary')}`}
        >
          <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[560px]">
            <Image
              src={quote.image}
              alt={language === 'en' ? quote.image_alt_en : quote.image_alt_cn}
              fill
              sizes="(max-width: 767px) 100vw, (max-width: 1280px) 45vw, 550px"
              className="object-cover object-[50%_40%]"
            />
          </div>
          <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
            <p
              className={`escape mb-7 text-xs font-bold uppercase tracking-[0.16em] ${getThemeTextClass(theme)}`}
            >
              {language === 'en' ? quote.label_en : quote.label_cn}
            </p>
            <blockquote className="text-2xl font-semibold leading-[1.4] tracking-tight sm:text-3xl lg:text-[2.1rem]">
              “{language === 'en' ? quote.quote_en : quote.quote_cn}”
            </blockquote>
            <figcaption className="mt-9 border-t border-[var(--color-card-border)] pt-6">
              <p className="escape font-semibold">
                {language === 'en'
                  ? quote.attribution_en
                  : quote.attribution_cn}
              </p>
              <p className="escape mt-1 text-sm leading-6 text-[var(--color-foreground-secondary)]">
                {language === 'en' ? quote.context_en : quote.context_cn}
              </p>
            </figcaption>
          </div>
        </figure>
      </div>
    </section>
  );
}
